import { Chip, useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { TimeIcon } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useWorktimeReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { DimensionFilters, DimensionsForm } from './DimensionsForm';
import { useWorktimeDimensionReport } from './useWorktimeDimensionReport';

export const WorktimeDimensionReport = () => {
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-height:650px)');

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const [dimensionFilters, setDimensionFilters] = useState<DimensionFilters>(
    {},
  );

  const defaultReportSelection: AnalyticsReportProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('month').toDate(),
      endDate: moment().toDate(),
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useWorktimeReportData<object>(reportDatasetProps.reportSelection);

  const { table } = useWorktimeDimensionReport({
    data,
    filters: dimensionFilters,
  });

  return (
    <ReportViewLayout
      heading={t('laWorktimeReportDimensions')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  return (
                    <>{`${moment(params?.startDate).format('L')} - ${moment(
                      params?.endDate,
                    ).format('L')}`}</>
                  );
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <ReportDateRangeSelector
                      startDate={params.startDate}
                      endDate={params.endDate}
                      onRangeChange={(startDate, endDate) =>
                        onChange({
                          ...params,
                          startDate: startDate ?? params.startDate,
                          endDate: endDate ?? params.endDate,
                        })
                      }
                      mode={ReportDateRangeSelectorMode.DateRange}
                    />
                  );
                }}
              />
              <DimensionsForm onApply={setDimensionFilters} />
              <Chip
                icon={<TimeIcon />}
                label={`${t('laTotal')}: ${table.summaryHours || '00:00'}`}
                sx={{ fontWeight: 'bold' }}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laWorktimeDimensionReport')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        initialState={{
          columns: {
            columnVisibilityModel: {
              empNumber: false,
              empUnitNumber: false,
              empUnitName: false,
              legalEntityCode: false,
              legalEntityName: false,
              costCenterCode: false,
              costCenterName: false,
            },
          },
        }}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        getRowId={(row) => row.uniqueId}
        pinnedColumns={{
          left: ['empId', 'empFirstName', 'empLastName'],
        }}
        columnHeaderHeight={36}
      />
    </ReportViewLayout>
  );
};
